// YERİ: src/app/domains/website/sections/mkt-a11y.ts
//
// Vitrinin EKRAN OKUYUCU etiketleri.
//
// Neden CMS'te değil: bunlar içerik değil, kontrol adı — "Önceki", "Menüyü aç".
// Editörün düzenleyeceği bir şey yok, ve bir dilde boş bırakılırsa sayfa
// erişilemez hâle gelir. CMS alanı yapmak onları kırılgan yapardı.
//
// Neden bileşenin içinde de değil: aynı altı dize altı ayrı dosyada Türkçe
// sabit duruyordu — Arapça sayfada ok butonu "Sonraki" diye okunuyordu.
//
// Batch 10 (Transloco) geldiğinde bu sözlük olduğu gibi çeviri dosyalarına
// taşınır; o zamana kadar tek doğru kaynak burasıdır.

/** Vitrinde ekran okuyucuya okunan sabit kontrol adları. */
export interface MktA11yLabels {
  /** Nokta butonu. {n} slayt numarasıyla değiştirilir. */
  slide: string;
  previous: string;
  next: string;
  dismissAnnouncement: string;
  mainMenu: string;
  /** Dil seçici. Etiket bu önekle dilin kendi adını birleştirir. */
  language: string;
}

const EN: MktA11yLabels = {
  slide: 'Slide {n}',
  previous: 'Previous',
  next: 'Next',
  dismissAnnouncement: 'Dismiss announcement',
  mainMenu: 'Main menu',
  language: 'Language',
};

const LABELS: Record<string, MktA11yLabels> = {
  en: EN,

  fr: {
    slide: 'Diapositive {n}',
    previous: 'Précédent',
    next: 'Suivant',
    dismissAnnouncement: 'Fermer l’annonce',
    mainMenu: 'Menu principal',
    language: 'Langue',
  },

  es: {
    slide: 'Diapositiva {n}',
    previous: 'Anterior',
    next: 'Siguiente',
    dismissAnnouncement: 'Cerrar el aviso',
    mainMenu: 'Menú principal',
    language: 'Idioma',
  },

  pt: {
    slide: 'Slide {n}',
    previous: 'Anterior',
    next: 'Seguinte',
    dismissAnnouncement: 'Fechar o aviso',
    mainMenu: 'Menu principal',
    language: 'Idioma',
  },

  tr: {
    slide: 'Slayt {n}',
    previous: 'Önceki',
    next: 'Sonraki',
    dismissAnnouncement: 'Duyuruyu kapat',
    mainMenu: 'Ana menü',
    language: 'Dil',
  },

  it: {
    slide: 'Diapositiva {n}',
    previous: 'Precedente',
    next: 'Successivo',
    dismissAnnouncement: 'Chiudi l’annuncio',
    mainMenu: 'Menu principale',
    language: 'Lingua',
  },

  de: {
    slide: 'Folie {n}',
    previous: 'Zurück',
    next: 'Weiter',
    dismissAnnouncement: 'Hinweis schließen',
    mainMenu: 'Hauptmenü',
    language: 'Sprache',
  },

  ru: {
    slide: 'Слайд {n}',
    previous: 'Назад',
    next: 'Вперёд',
    dismissAnnouncement: 'Закрыть объявление',
    mainMenu: 'Главное меню',
    language: 'Язык',
  },

  ar: {
    slide: 'الشريحة {n}',
    previous: 'السابق',
    next: 'التالي',
    dismissAnnouncement: 'إغلاق الإعلان',
    mainMenu: 'القائمة الرئيسية',
    language: 'اللغة',
  },

  fa: {
    slide: 'اسلاید {n}',
    previous: 'قبلی',
    next: 'بعدی',
    dismissAnnouncement: 'بستن اعلان',
    mainMenu: 'منوی اصلی',
    language: 'زبان',
  },

  ko: {
    slide: '슬라이드 {n}',
    previous: '이전',
    next: '다음',
    dismissAnnouncement: '공지 닫기',
    mainMenu: '주 메뉴',
    language: '언어',
  },

  vi: {
    slide: 'Trang chiếu {n}',
    previous: 'Trước',
    next: 'Tiếp',
    dismissAnnouncement: 'Đóng thông báo',
    mainMenu: 'Menu chính',
    language: 'Ngôn ngữ',
  },

  ja: {
    slide: 'スライド {n}',
    previous: '前へ',
    next: '次へ',
    dismissAnnouncement: 'お知らせを閉じる',
    mainMenu: 'メインメニュー',
    language: '言語',
  },

  zh: {
    slide: '幻灯片 {n}',
    previous: '上一张',
    next: '下一张',
    dismissAnnouncement: '关闭公告',
    mainMenu: '主菜单',
    language: '语言',
  },

  'zh-tw': {
    slide: '投影片 {n}',
    previous: '上一張',
    next: '下一張',
    dismissAnnouncement: '關閉公告',
    mainMenu: '主選單',
    language: '語言',
  },

  th: {
    slide: 'สไลด์ {n}',
    previous: 'ก่อนหน้า',
    next: 'ถัดไป',
    dismissAnnouncement: 'ปิดประกาศ',
    mainMenu: 'เมนูหลัก',
    language: 'ภาษา',
  },
};

/**
 * Dil kodunun etiketleri.
 *
 * Bölgesel kod ana dile düşer: 'en-ca' → 'en'. (en-ca vitrinde ayrıca
 * çevrilmiyor; yalnız para birimi ve yazım farklı.) Tanınmayan kod da
 * İngilizce'ye düşer — etiketsiz bir buton her zaman daha kötüdür.
 */
export function a11yLabels(langCode: string | null | undefined): MktA11yLabels {
  const code = (langCode ?? '').toLowerCase();
  return LABELS[code] ?? LABELS[code.split('-')[0]] ?? EN;
}
